import { resolveConnectIframeUrl } from './config'
import type { ConnectLink, ConnectOpenConfig } from './types'

let loaderPromise: Promise<void> | null = null
let resolvedSrc: string | null = null

const GLOBAL_POLL_MS = 50

export interface LoadOptions {
  /** Explicit Connect `iframe.js` URL. Overrides `env`. */
  src?: string
  /** Evaro environment. Resolves the Connect URL when `src` isn't given. */
  env?: string
  timeoutMs?: number
}

export function loadConnectIframeScript(options: LoadOptions = {}): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Connect loader requires a browser environment'))
  }

  if (typeof window.evaro_connect === 'function') {
    return Promise.resolve()
  }

  const src = resolveConnectIframeUrl({ src: options.src, env: options.env })
  if (loaderPromise && resolvedSrc === src) {
    return loaderPromise
  }

  resolvedSrc = src
  const timeoutMs = options.timeoutMs ?? 8000
  const promise = new Promise<void>((resolve, reject) => {
    let settled = false
    let loaded = false
    let inserted: HTMLScriptElement | null = null
    let timer: ReturnType<typeof setTimeout> | null = null
    let poll: ReturnType<typeof setInterval> | null = null
    const stop = () => {
      settled = true
      if (timer) clearTimeout(timer)
      if (poll) clearInterval(poll)
    }
    const settleOk = () => {
      if (settled) return
      stop()
      resolve()
    }
    const settleErr = (err: Error) => {
      if (settled) return
      stop()
      // A dead tag fires no second `load` or `error`, so a retry needs a fresh one.
      inserted?.remove()
      reject(err)
    }

    // iframe.js can define the global after an async step (Connect fetches its
    // config first), so `load` does not mean ready. Wait until the timeout.
    const waitForGlobal = () => {
      if (settled) return
      if (typeof window.evaro_connect === 'function') return settleOk()
      poll ??= setInterval(() => {
        if (typeof window.evaro_connect === 'function') settleOk()
      }, GLOBAL_POLL_MS)
    }
    const onError = () => settleErr(new Error(`Failed to load Connect script: ${src}`))

    const existing = document.querySelector<HTMLScriptElement>(`script[data-evaro-iframe="1"]`)
    if (existing) {
      // Its `load` may already be past, so poll for the global straight away.
      existing.addEventListener('error', onError, { once: true })
      waitForGlobal()
    } else {
      const script = document.createElement('script')
      script.src = src
      script.defer = true
      script.dataset.evaroIframe = '1'
      script.addEventListener(
        'load',
        () => {
          loaded = true
          waitForGlobal()
        },
        { once: true }
      )
      script.addEventListener('error', onError, { once: true })
      inserted = script
      document.head.appendChild(script)
    }

    // Safety net: if `load` never fires (CSP block silent failure, slow CDN),
    // reject after the timeout so callers don't hang forever.
    timer = setTimeout(() => {
      const reason = loaded
        ? `window.evaro_connect was not defined within ${timeoutMs}ms of loading ${src}`
        : `Connect script load timed out after ${timeoutMs}ms`
      settleErr(new Error(reason))
    }, timeoutMs)
  })

  // A failed load is not cached, so the next call can try again.
  loaderPromise = promise
  promise.catch(() => {
    if (loaderPromise === promise) loaderPromise = null
  })
  return promise
}

export async function openConnect(link: ConnectLink, config?: ConnectOpenConfig): Promise<void> {
  if (!link) throw new Error('openConnect requires a link')
  await loadConnectIframeScript({ env: config?.env })
  if (typeof window.evaro_connect !== 'function') {
    throw new Error('window.evaro_connect not available after loading iframe.js')
  }
  window.evaro_connect(link, config?.patient ? { patient: config.patient } : undefined)
}

/**
 * Open Connect for `link` and dispatch the matching `evaro:open` / `evaro:error`
 * CustomEvents on `target`. Shared by `<evaro-landing>` and `<evaro-connect>` —
 * keeps the open + telemetry contract in one place.
 */
export function dispatchConnectOpen(target: EventTarget, link: ConnectLink, env?: string): void {
  openConnect(link, env ? { env } : undefined).catch(err => {
    console.error('Failed to open Connect:', err)
    target.dispatchEvent(new CustomEvent('evaro:error', { detail: { error: err }, bubbles: true }))
  })
  target.dispatchEvent(new CustomEvent('evaro:open', { detail: { link }, bubbles: true }))
}

// Visible for tests
export function __resetConnectLoaderForTests() {
  loaderPromise = null
  resolvedSrc = null
}
